import { useEffect, useState, useRef } from 'react';
// @types
import { IChatConversation } from '../../../../@types/chat';
//
import Scrollbar from '../../../../components/scrollbar';
import Lightbox from '../../../../components/lightbox';
//
import ChatMessageItem from './ChatMessageItem';

// ----------------------------------------------------------------------

type Props = {
  conversation: IChatConversation;
};

export default function ChatMessageList({ conversation }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const [selectedImage, setSelectedImage] = useState<number>(-1);

  useEffect(() => {
    const scrollMessagesToBottom = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    };
    scrollMessagesToBottom();
  }, [conversation.messages]);

  const imagesLightbox = conversation.messages
    .filter((messages) => messages.contentType === 'image')
    .map((messages) => ({ src: messages.body }));

  const handleOpenLightbox = (imageUrl: string) => {
    const imageIndex = imagesLightbox.findIndex((image) => image.src === imageUrl);
    setSelectedImage(imageIndex);
  };

  const handleCloseLightbox = () => {
    setSelectedImage(-1);
  };

  // Group messages sent within 30 seconds by the same sender (more restrictive grouping)
  const shouldGroupWithPrevious = (currentMessage: any, previousMessage: any, messageIndex: number): boolean => {
    if (!previousMessage) return false;
    if (currentMessage.senderId !== previousMessage.senderId) return false;
    
    // More restrictive time window: 30 seconds instead of 1 minute
    const timeDiff = Math.abs(new Date(currentMessage.createdAt).getTime() - new Date(previousMessage.createdAt).getTime());
    if (timeDiff > 30000) return false; // 30 seconds in milliseconds
    
    // Limit group size: don't group if we already have 4+ messages in current group
    let groupSize = 1;
    for (let i = messageIndex - 1; i >= 0; i--) {
      const prevMsg = conversation.messages[i];
      const evenPrevMsg = i > 0 ? conversation.messages[i - 1] : null;
      
      if (!shouldGroupBasic(prevMsg, evenPrevMsg)) break;
      groupSize++;
      if (groupSize >= 4) return false; // Max 4 messages per group
    }
    
    return true;
  };
  
  // Helper function for basic grouping check (to avoid infinite recursion)
  const shouldGroupBasic = (currentMessage: any, previousMessage: any): boolean => {
    if (!previousMessage) return false;
    if (currentMessage.senderId !== previousMessage.senderId) return false;
    const timeDiff = Math.abs(new Date(currentMessage.createdAt).getTime() - new Date(previousMessage.createdAt).getTime());
    return timeDiff <= 30000;
  };

  return (
    <>
      <Scrollbar
        scrollableNodeProps={{
          ref: scrollRef,
        }}
        sx={{ p: 3, height: 1 }}
      >
        {conversation.messages.map((message, index) => {
          const previousMessage = index > 0 ? conversation.messages[index - 1] : null;
          const isGrouped = shouldGroupWithPrevious(message, previousMessage, index);
          
          return (
            <ChatMessageItem
              key={message.id}
              message={message}
              conversation={conversation}
              onOpenLightbox={() => handleOpenLightbox(message.body)}
              isGrouped={isGrouped}
            />
          );
        })}
      </Scrollbar>

      <Lightbox
        index={selectedImage}
        slides={imagesLightbox}
        open={selectedImage >= 0}
        close={handleCloseLightbox}
      />
    </>
  );
}
